/**
 * staffOptions.ts
 *
 * Shared plumbing for the Physician / ECC / Provider selects, which are backed by
 * GET /v5/{doctors,coordinators,nurses}.
 *
 * Those endpoints only offer people who are still active users. That is what we
 * want everywhere except one case: a record already assigned to someone who has
 * since been de-activated. Drop them from that record's own dropdown and the field
 * renders the placeholder — indistinguishable from unassigned — while the form
 * state quietly keeps their id and saves it straight back.
 *
 * So callers pass the ids their record currently holds as `includeIds`. The server
 * returns those rows whatever their state, flagged `active: false`, and
 * `buildStaffOptions` labels them so the reader can see why that name is not in
 * the rest of the list.
 */

export interface StaffOption<T> {
  value: T;
  label: string;
}

/** A staff row as the three list services return it. */
interface StaffRow {
  active?: boolean;
}

/**
 * Ids a record already holds, cleaned up for the query string: positive, unique
 * and sorted, so the same selection always produces the same value.
 */
export function toIncludeIds(...ids: Array<number | null | undefined>): number[] {
  return Array.from(new Set(ids.map((id) => Number(id) || 0).filter((id) => id > 0))).sort(
    (a, b) => a - b,
  );
}

/**
 * Cache key for a staff list.
 *
 * `includeIds` varies per record, and the plain ['doctors', 'active'] key is shared
 * by every staff select in the app — so without the ids in the key one plan's
 * retained-but-inactive physician would be served to every other component from
 * cache. Ids are normalised first so two orderings of the same selection still hit.
 *
 * `scope` separates the two shapes of list: 'active' for assignment selectors and
 * 'all' for filter lists, which hold a strictly larger set. Sharing one key between
 * them would leak departed staff into the assignment selectors.
 */
export function staffQueryKey(
  base: string,
  includeIds: number[] = [],
  scope: 'active' | 'all' = 'active',
): (string | number)[] {
  return [base, scope, toIncludeIds(...includeIds).join(',')];
}

/**
 * Build the select options, marking anyone the server flagged inactive.
 *
 * Only a person retained via `includeIds` can come back inactive, so the suffix is
 * never noise: it appears exactly on the assignee who has left.
 */
export function buildStaffOptions<T extends StaffRow, V>(
  rows: T[],
  getValue: (row: T) => V,
  getLabel: (row: T) => string,
): StaffOption<V>[] {
  return rows
    .map((row) => {
      const name = getLabel(row) || 'Unnamed';

      return {
        value: getValue(row),
        label: row.active === false ? `${name} (inactive)` : name,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
